import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  InputAdornment,
  Modal,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, itIT } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import {
  EstazioneSospesiFormValues,
  FormatGrigliaOrariPerGestioneSospesi,
} from "../../../services/gestioneSospesi";
import { Doctor, Typology } from "../../../services/interfaces";
import {
  CommonFieldsData,
  doctor,
  estrSospesi,
  spostaPren,
  typology,
  datiPacadc,
} from "../../../networking";
import { formatHour } from "../../AppointmentResearch/AppointmentResearch";
import { ModalitaSospesi } from "../AppointmentManagement";
import { AllertMessage } from "../../DoctorsList/Medint/MedintDetails/MedintDetails";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

type CancellazioneFormValues = {
  daPagare: boolean;
  sospeso: boolean;
  noteCancellazione: string;
};

interface AppuntamentoSospesoModificato {
  idAppSospeso: string;
  dataSpostamento: string;
  oraSpostamento: string;
  medicoSpostamento: string;
  agendaSpostamento?: string;
  medicoSostituto?: string;
}

interface PayloadInput {
  dataInizio: string;
  dataFine: string;
  codMedint: string;
  codTpgpre: string;
}

const useFormGestioneSospesi =
  (): UseFormReturn<EstazioneSospesiFormValues> => {
    return useForm<EstazioneSospesiFormValues>({
      defaultValues: {
        codMedint: null,
        codTpgpre: null,
        dataFine: "",
        dataInizio: "",
      },
      mode: "onSubmit",
    });
  };

const AppuntamentiSospesi = (props: {
  listaAppuntamentiSospesi: any[];
  appuntamentiSospesiSpostati: AppuntamentoSospesoModificato[];
  selectedSlotOrario: any[];
  selectedAppuntamentiSospesi: any[];
  setSelectedAppuntamentoSospeso: React.Dispatch<React.SetStateAction<any[]>>;
  setListaAppuntamentiSospesi: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
  const columns: GridColDef[] = [
    {
      field: "data",
      headerName: "Data",
      editable: false,
      flex: 0.3,
      minWidth: 80,
      valueFormatter: (params) =>
        params.value &&
        dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).isValid()
          ? dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("DD/MM/YY")
          : "",
    },
    {
      field: "ora",
      headerName: "Ora",
      editable: false,
      flex: 0.3,
      minWidth: 60,
    },
    {
      field: "durata",
      headerName: "D",
      editable: false,
      flex: 0.3,
      minWidth: 40,
    },
    {
      field: "tipologia",
      headerName: "T",
      editable: false,
      flex: 0.3,
      minWidth: 40,
    },
    {
      field: "paziente",
      headerName: "Paziente",
      editable: false,
      flex: 1,
    },
    {
      field: "telefono",
      headerName: "Telefono",
      editable: false,
      flex: 1,
    },
    {
      field: "esame",
      headerName: "Esame",
      editable: false,
      flex: 1,
    },
    { field: "medico", headerName: "Medico", editable: false, flex: 1 },
    {
      field: "nuovaData",
      headerName: "Nuova Data",
      editable: false,
      flex: 1,
      valueFormatter: (params) =>
        params.value &&
        dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).isValid()
          ? dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("DD/MM/YY")
          : "",
    },
    {
      field: "nuovaOra",
      headerName: "Nuova Ora",
      editable: false,
      flex: 1,
      valueFormatter: (params) =>
        params.value ? formatHour(params.value) : "",
    },
    {
      field: "note",
      headerName: "Note",
      editable: false,
      flex: 1,
    },
    {
      field: "sostituto",
      headerName: "Sostituto",
      editable: false,
      flex: 1,
    },
  ];

  interface DataFormatForGridView {
    idpacadc: string;
    data: string;
    durata: string;
    tipologia: string;
    paziente: string;
    telefono: string;
    esame: string;
    ora: string;
    medico: string;
    nuovaData: string;
    nuovaOra: string;
    note: string;
    sostituto: string;
  }

  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    setRows(
      props.listaAppuntamentiSospesi.map((appuntamento: any) => {
        const appSpostato = props.appuntamentiSospesiSpostati.find(
          (e) => e.idAppSospeso === appuntamento.IDPACADC
        );

        const tempFormat: DataFormatForGridView = {
          idpacadc: appuntamento.IDPACADC,
          data: appuntamento.DATAVISITA,
          durata: appuntamento.DURATA,
          tipologia: appuntamento.CODTIPOLOGIA,
          paziente: appuntamento.COGNOMEPAZIENTE.concat(
            " ",
            appuntamento.NOMEPAZIENTE
          ),
          telefono: appuntamento.NUMTEL,
          esame: appuntamento.DESCPRESTAZIONE,
          ora: appuntamento.ORAVISITA ?? "",
          medico: appuntamento.NOMINMEDICO,
          nuovaData: appSpostato?.dataSpostamento ?? "",
          nuovaOra: appSpostato?.oraSpostamento ?? "",
          note: appuntamento.NOTEPREN,
          sostituto: appSpostato?.medicoSostituto ?? "", 
        };

        return tempFormat;
      })
    );
  }, [props.listaAppuntamentiSospesi, props.appuntamentiSospesiSpostati]);

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", width: "100%" }}
      borderRadius={2}
    >
      <DataGrid
        sx={{
          border: "1px solid #ccc",
          width: "100%",
          borderRadius: 2,
          display: "flex",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#003267",
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#F8F9FC",
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
            // display: "none",
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-sortIcon": {
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none",
            },
        }}
        rows={rows}
        columns={columns}
        headerHeight={36}
        rowHeight={36}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        hideFooter
        getRowId={(row) => row.idpacadc}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        checkboxSelection
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu
        getCellClassName={(params) => {
          if (params.field === "ora") {
            if (params.formattedValue)
              params.formattedValue = formatHour(params.value);
          }
          return "";
        }}
        onSelectionModelChange={(ids) => {
          props.setSelectedAppuntamentoSospeso(ids);
        }}
      />
    </Box>
  );
};

const SlotDisponibili = (props: {
  listaOrariDettaglio: FormatGrigliaOrariPerGestioneSospesi[];
  selectedAppuntamentiSospesi: string[];
  selectedSlotOrario: string[];
  setSelectedSlotOrario: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
  const columns: GridColDef[] = [
    {
      field: "data",
      headerName: "Data",
      editable: false,
      flex: 1,
      valueFormatter: (params) =>
        params.value &&
        //Controllo la validità della data e formatto in DD/MM/YY
        dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).isValid()
          ? dayjs(params.value, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("DD/MM/YY")
          : "",
    },
    {
      field: "ora",
      headerName: "Ora",
      editable: false,
      flex: 1,
    },
    { field: "medico", headerName: "Medico", editable: false, flex: 1 },
  ];

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", width: "100%" }}
      borderRadius={2}
    >
      <DataGrid
        sx={{
          border: "1px solid #ccc",
          width: "100%",
          borderRadius: 2,
          display: "flex",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#003267",
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#F8F9FC",
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
            display: "none",
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-sortIcon": {
            color: "#FFFFFF",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none",
            },
          "& .cell-checked-disabled.MuiDataGrid-cellCheckbox *": {
            pointerEvents: "none",
          },
        }}
        rows={props.listaOrariDettaglio}
        columns={columns}
        getRowId={(row) => row.ora}
        headerHeight={36}
        rowHeight={36}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        hideFooter
        isRowSelectable={({ row }) =>
          props.selectedSlotOrario?.length <
            props.selectedAppuntamentiSospesi?.length ||
          props.selectedSlotOrario?.includes(row.ora)
        }
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        checkboxSelection
        disableColumnFilter
        disableColumnMenu
        getCellClassName={(params) => {
          if (params.field === "ora") {
            params.formattedValue = formatHour(params.value);
          }

          if (props.selectedSlotOrario.includes(String(params?.id))) {
            return "cell-checked-disabled";
          } else {
            return "";
          }
        }}
        onSelectionModelChange={props.setSelectedSlotOrario}
      />
    </Box>
  );
};

const RicercaAppuntamenti = (props: {
  methods: UseFormReturn<EstazioneSospesiFormValues>;
  setListaAppuntamentiSospesi: React.Dispatch<React.SetStateAction<any[]>>;
  payloadInput: React.MutableRefObject<PayloadInput | null>;
  sendPayload: () => Promise<void>;
  loadingOptions: boolean;
  listaMedici: [];
  listaTipologie: [];
  appSospesi_Dal: string;
  appSospesi_Al: string;
  medico: Doctor | null;
  tipologia: Typology | null;
}) => {
  useEffect(() => {
    props.payloadInput.current = {
      dataInizio: props.appSospesi_Dal ?? "",
      dataFine: props.appSospesi_Al ?? "",
      codMedint: props.medico?.CODICE ?? "",
      codTpgpre: props.tipologia?.CODICE ?? "",
    };
  }, [
    props.appSospesi_Dal,
    props.appSospesi_Al,
    props.medico,
    props.tipologia,
  ]);

  return (
    <Grid
      container
      border={"1px solid #ccc"}
      p={2}
      gap={2}
      borderRadius={2}
      sx={{ display: "flex", justifyContent: "center" }}
    >
      <Grid
        item
        xs={12}
        gap={2}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Controller
          name="dataInizio"
          control={props.methods.control}
          rules={{
            required: "Campo obbligatorio",
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="App. Sospesi dal"
              placeholder="Inserire Data"
              required
              inputProps={{
                max: props.appSospesi_Al ?? "9999-12-31",
              }}
              type="date"
              value={field.value ?? ""}
              fullWidth
              error={!!fieldState.error}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <Controller
          name="dataFine"
          control={props.methods.control}
          rules={{
            required: "Campo obbligatorio",
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="App. Sospesi al"
              placeholder="Inserire Data"
              required
              inputProps={{
                min: props.appSospesi_Dal ?? "",
                max: "9999-12-31",
              }}
              type="date"
              value={field.value ?? ""}
              fullWidth
              error={!!fieldState.error}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        {props.loadingOptions ? (
          <TextField
            label="Medico"
            variant="outlined"
            size="small"
            disabled
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ bgcolor: "transparent", gap: 2 }}
                >
                  <CircularProgress size={"1rem"} />
                  {` Caricamento.. `}
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Controller
            name={"codMedint"}
            control={props.methods.control}
            render={({ field }) => (
              <Autocomplete
                autoComplete
                autoHighlight
                componentsProps={{
                  clearIndicator: {
                    title: "Pulisci selezione",
                  },
                }}
                size="small"
                options={props.listaMedici ?? []}
                getOptionLabel={(option: any) => option.CODDESCR || ""}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.DESCRIZIONE === value.DESCRIZIONE
                }
                onChange={(_, data: any) => {
                  field.onChange(data);
                }}
                value={field.value || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Medico"
                    placeholder="Selezionare un medico"
                    fullWidth
                  />
                )}
              />
            )}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        {props.loadingOptions ? (
          <TextField
            label="Tipo"
            variant="outlined"
            size="small"
            disabled
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ bgcolor: "transparent", gap: 2 }}
                >
                  <CircularProgress size={"1rem"} />
                  {` Caricamento.. `}
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Controller
            name={"codTpgpre"}
            control={props.methods.control}
            render={({ field }) => (
              <Autocomplete
                autoComplete
                autoHighlight
                componentsProps={{
                  clearIndicator: {
                    title: "Pulisci selezione",
                  },
                }}
                size="small"
                options={props.listaTipologie ?? []}
                getOptionLabel={(option) => option.DESCRIZIONE || ""}
                isOptionEqualToValue={(option, value) =>
                  option.DESCRIZIONE === value.DESCRIZIONE
                }
                onChange={(_, data) => {
                  field.onChange(data);
                }}
                value={field.value || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo"
                    placeholder="Tutti"
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={params ?? ""}
                  />
                )}
              />
            )}
          />
        )}
      </Grid>
      <Grid
        item
        xs={12}
        gap={2}
        display={"flex"}
        justifyContent={"space-between"}
      >
        <Button variant="outlined" onClick={() => props.methods.reset()}>
          Reset Filtri
        </Button>
        <Button
          variant="contained"
          onClick={props.methods.handleSubmit(props.sendPayload)}
        >
          Ricerca
        </Button>
      </Grid>
    </Grid>
  );
};

const GestioneSospesi = (props: {
  listaOrariDettaglio: FormatGrigliaOrariPerGestioneSospesi[];
  modalitaSospesi: ModalitaSospesi;
  setModalitaSospesi: React.Dispatch<React.SetStateAction<ModalitaSospesi>>;
  setListaOrariDettaglio: React.Dispatch<
    React.SetStateAction<FormatGrigliaOrariPerGestioneSospesi[]>
  >;
  refreshAppointments: () => void;
}) => {
  const [deleting, setDeleting] = useState(false);
  const methods = useFormGestioneSospesi();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [listaMedici, setListaMedici] = useState<[]>([]);
  const [listaTipologie, setListaTipologie] = useState<[]>([]);
  const [listaAppuntamentiSospesi, setListaAppuntamentiSospesi] = useState<
    any[]
  >([]);

  const [showAllert, setShowAllert] = useState<boolean>(false);
  const [severity, setSeverity] = useState<string>("warning");
  const [message, setMessage] = useState<string>(
    "Confermare la riassegnazione dei sospesi selezionati?"
  );

  // ids dei sospesi selezionati
  const [selectedAppuntamentiSospesi, setSelectedAppuntamentoSospeso] =
    useState<string[]>([]);
  // ids degli slot selezionati
  const [selectedSlotOrario, setSelectedSlotOrario] = useState<string[]>([]);
  const [appuntamentiSospesiSpostati, setAppuntamentiSospesiSpostati] =
    useState<AppuntamentoSospesoModificato[]>([]);
  const [showModaleAnnullamentoLogico, setShowModaleAnnullamentoLogico] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const appSospesi_Dal = methods.watch("dataInizio");
  const appSospesi_Al = methods.watch("dataFine");
  const medico = methods.watch("codMedint");
  const tipologia = methods.watch("codTpgpre");

  const payloadInput = useRef<PayloadInput | null>(null);

  const sendPayload = async () =>
    await estrSospesi(payloadInput.current).then((response) => {
      if (response.data.esito === "OK")
        setListaAppuntamentiSospesi(response.data.dati.prenSospese);
    });

  useEffect(() => {
    const nuoviAppuntamenti: AppuntamentoSospesoModificato[] = [];

    selectedSlotOrario.forEach((slotOrarioId, index) => {
      const slotOrario = props.listaOrariDettaglio.find(
        (slot) => slot.ora === slotOrarioId
      );
      if (!slotOrario) return;

      const appuntamentoSospesoId = selectedAppuntamentiSospesi.at(index);
      console.log({ appuntamentoSospesoId });
      console.log({ listaAppuntamentiSospesi });

      const appuntamentoSospeso = listaAppuntamentiSospesi.find(
        (appuntamentoSospeso) =>
          appuntamentoSospeso.IDPACADC === appuntamentoSospesoId
      );
      if (!appuntamentoSospeso) return;

      nuoviAppuntamenti.push({
        idAppSospeso: appuntamentoSospeso.IDPACADC,
        dataSpostamento:
          dayjs(slotOrario.data, "DD/MM/YYYY").format("YYYY-MM-DD") ?? "",
        oraSpostamento: slotOrario?.ora ?? "",
        medicoSpostamento: slotOrario?.codiceMedico ?? "",
        agendaSpostamento: "",
        medicoSostituto: slotOrario?.medico ?? "",
      });
    });

    setAppuntamentiSospesiSpostati(nuoviAppuntamenti);
  }, [selectedSlotOrario]);

  useEffect(() => {
    setSelectedSlotOrario((prev) => {
      const temp = appuntamentiSospesiSpostati.filter((a) =>
        selectedAppuntamentiSospesi.includes(a.idAppSospeso)
      );
      return prev.filter((e) => temp.map((a) => a.oraSpostamento).includes(e));
    });
  }, [selectedAppuntamentiSospesi]);

  // Fetch di aggiornamento dei campi di Tipologie e Medici
  const fetchData = async () => {
    setLoadingOptions(true);
    try {
      const mediciRequest: CommonFieldsData = {
        listino: "",
        tipoAgenda: tipologia?.CODICE ?? "",
        specialita: "",
        prestazione: "",
        medico: [],
      };
      const doctorResponse = await doctor(mediciRequest);
      const tipologiaRequest: CommonFieldsData = {
        listino: "",
        tipoAgenda: "",
        specialita: "",
        prestazione: "",
        medico: [medico?.CODICE ?? ""],
      };
      const typologyResponse = await typology(tipologiaRequest);

      setListaMedici(doctorResponse.data.dati);
      setListaTipologie(typologyResponse.data.dati);
    } catch (error) {
      console.error("Errore nel fetch:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [medico, tipologia]);

  // funzione di submit dei valori modificati dell'appuntamento
  const handleDataToSubmit = async () => {
    setShowAllert(true);
    try {
      const response = await spostaPren({
        appuntamenti: appuntamentiSospesiSpostati,
      });
      console.log(response);
      if (response.data.esito === "OK") {
        setMessage(
          response.data.messaggio === "" ? response.data.messaggio : ""
        );
        setSeverity("success");
        props.setListaOrariDettaglio((prev) =>
          prev.filter((e) => !selectedSlotOrario.includes(e.ora))
        );
        sendPayload();
        console.log("operazione conclusa a buon fine");
      } else {
        setMessage(
          response.data.messaggio === "" ? response.data.messaggio : ""
        );
        setSeverity("error");
      }
    } catch (error) {
      console.error("Errore nella submit:", error);
    } finally {
      setTimeout(() => {
        setShowAllert(false);
      }, 4000);
      setTimeout(() => {
        setSeverity("warning");
        setMessage("Confermare la riassegnazione dei sospesi selezionati?");
      }, 5000);
    }
  };

  const propsSlotDisponibili = {
    listaOrariDettaglio: props.listaOrariDettaglio,
    selectedAppuntamentiSospesi,
    selectedSlotOrario,
    setSelectedSlotOrario,
  };

  const propsRicercaAppuntamenti = {
    methods,
    listaMedici,
    listaTipologie,
    appSospesi_Dal,
    appSospesi_Al,
    medico,
    tipologia,
    payloadInput,
    loadingOptions,
    setListaAppuntamentiSospesi,
    sendPayload,
  };

  const propsAppuntamentiSospesi = {
    listaAppuntamentiSospesi,
    setListaAppuntamentiSospesi,
    selectedSlotOrario,
    selectedAppuntamentiSospesi,
    setSelectedAppuntamentoSospeso,
    appuntamentiSospesiSpostati,
  };

  // --- Cancellazione (modale) ---
  const methodsFormCancellazione = useForm<CancellazioneFormValues>({
    defaultValues: {
      daPagare: false,
      sospeso: false,
      noteCancellazione: "",
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
    shouldFocusError: true,
  });

  //Per eliminare gli appuntamenti sospesi
  const onSubmitFormCancellazione = async (values: CancellazioneFormValues) => {
    //Nel caso non si selezioni nessun appuntamento
    if (selectedAppuntamentiSospesi.length === 0) {
      setSeverity("warning");
      setMessage("Seleziona almeno un appuntamento da cancellare.");
      setShowAllert(true);
      setTimeout(() => setShowAllert(false), 3000);
      return;
    }
    setLoading(true);
    try {
      setDeleting(true);

      // Eseguo in SEQUENZA (await dentro for...of)
      for (const idPacadc of selectedAppuntamentiSospesi) {
        const payload: any = {
          idPacadc,
          crud: "D",
          NOTE: values.noteCancellazione?.trim() || undefined,
          SWDAPAGARE: values.daPagare ? "1" : "0",
          SWSOSPESO: values.sospeso ? "1" : "0",
        };

        const resp = await datiPacadc(payload);
        const esito = resp?.data?.esito;
        if (esito !== "OK") {
          const msg = resp?.data?.messaggio || "Errore in cancellazione.";
          throw new Error(msg);
        }
      }

      // Se OK: aggiorno lo stato locale rimuovendo le righe cancellate
      setListaAppuntamentiSospesi((prev) =>
        prev.filter(
          (r) => !selectedAppuntamentiSospesi.includes(String(r.IDPACADC))
        )
      );

      // Pulizia selezioni
      setSelectedAppuntamentoSospeso([]);
      setSelectedSlotOrario([]);

      // Invio il messaggio all'utente con l'allert
      setSeverity("success");
      setMessage("Cancellazione completata con successo.");
      setShowAllert(true);

      // Chiudo il modale e resetto il form
      setShowModaleAnnullamentoLogico(false);
      methodsFormCancellazione.reset();

      //Per riallineare la vista
      props.refreshAppointments?.();
    } catch (err: any) {
      setSeverity("error");
      setMessage(err?.message || "Errore durante la cancellazione.");
      setShowAllert(true);
    } finally {
      setDeleting(false);
      setTimeout(() => setShowAllert(false), 4000);
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        open={loading}
        sx={(theme) => ({
          zIndex: theme.zIndex.modal + 1,
        })}
      >
        <CircularProgress />
      </Backdrop>

      <Modal open={showModaleAnnullamentoLogico}>
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "150vh",
            width: "85%",
            overflow: "auto",
            paddingTop: 2,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Tabs value={0} sx={{ paddingLeft: 2 }}>
            <Tab
              sx={{
                fontFamily: "Montserrat",
                fontWeight: "bold",
                textTransform: "none",
              }}
              label="Cancellazione"
            />
          </Tabs>

          <form
            onSubmit={methodsFormCancellazione.handleSubmit(
              onSubmitFormCancellazione
            )}
          >
            <Grid container spacing={2} padding={2} justifyContent="center">
              <Grid item xs={2}>
                <Controller
                  name="daPagare"
                  control={methodsFormCancellazione.control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                Da pagare
              </Grid>

              <Grid item xs={2}>
                <Controller
                  name="sospeso"
                  control={methodsFormCancellazione.control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                Sospeso
              </Grid>

              <Grid item xs={8}></Grid>

              <Grid item xs={12}>
                <Controller
                  name="noteCancellazione"
                  control={methodsFormCancellazione.control}
                  rules={{ required: "Campo obbligatorio" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      onBlur={field.onBlur}
                      label="Note di cancellazione"
                      fullWidth
                      multiline
                      rows={3}
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Button
                    variant="outlinedError"
                    onClick={() => setShowModaleAnnullamentoLogico(false)}
                    disabled={deleting}
                  >
                    Uscita
                  </Button>

                  <Button type="submit" variant="contained" disabled={deleting}>
                    {deleting
                      ? "Annullamento in corso..."
                      : "Conferma Cancellazione"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>

      <Grid container sx={{ backgroundColor: "#F8F8F8", p: 3 }}>
        <AllertMessage
          showAllert={showAllert}
          setShowAllert={setShowAllert}
          severity={severity}
          message={message}
          submitFunction={handleDataToSubmit}
          // showSummary={props.handleSummaryTableOpening}
        />
        <Grid item xs={12} mb={2}>
          <Typography sx={{ fontWeight: "bold" }}>Gestione Sospesi</Typography>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={selectedTab} onChange={() => setSelectedTab(0)}>
            <Tab
              sx={{
                fontFamily: "Montserrat",
                fontWeight: "bold",
                textTransform: "none",
              }}
              label="Dettaglio"
            />
          </Tabs>
        </Grid>
        <Grid item xs={6} pt={3} pb={1}>
          <Typography sx={{ fontWeight: "bold", color: "#00000099" }}>
            Ricerca Appuntamenti
          </Typography>
        </Grid>
        <Grid item xs={6} pt={3} pb={1}>
          <Typography sx={{ fontWeight: "bold", color: "#00000099" }}>
            Slot Disponibili
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex" }} pr={2}>
          <RicercaAppuntamenti {...propsRicercaAppuntamenti} />
        </Grid>
        <Grid item xs={6} sx={{ display: "flex" }}>
          <SlotDisponibili {...propsSlotDisponibili} />
        </Grid>
        <Grid
          item
          xs={12}
          mt={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignContent: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold", color: "#00000099" }}>
            Appuntamenti sospesi
          </Typography>
          <Button
            variant="outlinedError"
            onClick={() => {
              if (selectedAppuntamentiSospesi.length === 0) {
                setSeverity("warning");
                setMessage(
                  "Seleziona almeno un appuntamento prima di cancellare."
                );
                setShowAllert(true);
                setTimeout(() => setShowAllert(false), 3000);
                return;
              }
              setShowModaleAnnullamentoLogico(true);
            }}
          >
            Cancella Sospesi
          </Button>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", height: 200 }} mt={2}>
          <AppuntamentiSospesi {...propsAppuntamentiSospesi} />
        </Grid>
        <Grid item xs={12} mt={2}>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button
                variant="outlinedError"
                onClick={() => {
                  props.setModalitaSospesi((prev) => ({
                    ...prev,
                    view: false,
                  }));
                  props.refreshAppointments();
                }}
              >
                Indietro
              </Button>
            </Grid>
            <Grid item>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="outlined">Estrazione</Button>
                <Button variant="outlined">Prenotazione</Button>
                <Button
                  variant="contained"
                  onClick={() => setShowAllert(true)}
                  disabled={
                    selectedAppuntamentiSospesi.length < 1 ||
                    selectedSlotOrario.length < 1
                  }
                >
                  Riassegna
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default GestioneSospesi;
